import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  FormattedDate,
  isNumeric,
  TranslatedText,
  useDebouncedValue,
  useRouterPush,
  useToggle,
  useTranslator,
  XOR
} from '@ianesthesie-kit/core';
import {
  PatientSearchResult,
  useSearchSurgicalEvents
} from '@/modules/surgicalEvent';
import {
  Autocomplete,
  Chip,
  Popper,
  Stack,
  ToggleButton,
  Tooltip,
  Typography
} from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { SearchField } from '@/components/search';
import { themeColors } from '@ianesthesie-kit/config';
import { HospitalIcon } from '@/components/icon';
import { useUpdateUserAppSettingsMutation, useUserAppSetting } from '@/modules';
import { updateProtocolSearchKey } from '@/modules/appContext';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppDispatch } from '@/hooks';
import {
  PendingAppointment,
  PendingPatient,
  usePendingAppointmentsQuery
} from '@/modules/pendingPatient';
import { type UseQueryResult } from '@/store';
import { ID, ListApiResponse } from '@ianesthesie-kit/services';
import { useDialog } from '@ianesthesie-kit/redux';
import { AppDialogType } from '@/config';

type MixedPatientSearchResult = Omit<PatientSearchResult, 'patientId'> &
  XOR<
    {
      patientId: ID;
    },
    { pendingPatient: PendingPatient; pendingAppointmentId: ID }
  >;

const StyledPopper = styled(Popper)`
  min-width: ${(p) => `${p.style?.width || 1}px`};
`;

export function SearchPatientField({
  globalSearchModeOnly
}: {
  globalSearchModeOnly?: boolean;
}) {
  const push = useRouterPush();
  const dispatch = useAppDispatch();
  const { showDialog } = useDialog();
  const translate = useTranslator();
  const [value, setValue] = useState('');
  const debouncedValue = useDebouncedValue(value, 800);
  const t = useUserAppSetting('patientSearch', 'global');
  const searchMode = globalSearchModeOnly ? 'global' : t;
  const { data: surgicalEventResults, isFetching: isFetchingSurgicalEvents } =
    useSearchSurgicalEvents(
      {
        searchKey: debouncedValue
      },
      {
        skip: !debouncedValue || searchMode !== 'global'
      }
    );

  const selectPendingPatients = useCallback(
    (res: UseQueryResult<ListApiResponse<PendingAppointment>, any>) => {
      const appointments = res.data?.data;
      let target: MixedPatientSearchResult['target'];
      if (debouncedValue?.includes('@')) {
        target = 'EMAIL';
      } else if (isNumeric(debouncedValue)) {
        target = 'PHONE_NUMBER';
      } else if (/^\d+[-/]\d+[-/]\d+$/.test(debouncedValue ?? '')) {
        target = 'BIRTHDATE';
      } else {
        target = 'NAME';
      }
      const data: MixedPatientSearchResult[] | undefined = appointments
        ?.filter((pa) => !pa.pendingPatient.activated)
        .map((pa) => ({
          firstName: pa.pendingPatient.firstName,
          lastName: pa.pendingPatient.lastName,
          name: pa.pendingPatient.name,
          gender: pa.pendingPatient.gender,
          birthDate: pa.pendingPatient.birthDate,
          pendingPatient: pa.pendingPatient,
          pendingAppointmentId: pa.id,
          target
        }));

      return {
        ...res,
        data,
        currentData: data
      };
    },
    [debouncedValue]
  );

  const {
    data: pendingPatientResults,
    isFetching: isFetchingPendingAppointments
  } = usePendingAppointmentsQuery(
    {
      pageSize: 10,
      page: 0,
      query: debouncedValue
    },
    {
      skip: !debouncedValue || searchMode !== 'global',
      selectFromResult: selectPendingPatients
    }
  );

  const results: MixedPatientSearchResult[] = useMemo(
    () => [...(surgicalEventResults ?? []), ...(pendingPatientResults ?? [])],
    [surgicalEventResults, pendingPatientResults]
  );

  useEffect(() => {
    const protocolSearchKey = searchMode === 'global' ? '' : debouncedValue;
    dispatch(updateProtocolSearchKey(protocolSearchKey));
  }, [debouncedValue, searchMode, dispatch]);

  const loading =
    isFetchingPendingAppointments ||
    isFetchingSurgicalEvents ||
    (!!value && debouncedValue !== value);
  const {
    toggledOn: popupOpen,
    toggleOn: openPopup,
    toggleOff: closePopup
  } = useToggle();

  const showActivationDialog = (v: MixedPatientSearchResult) => {
    showDialog({
      dialogType: AppDialogType.ACTIVATE_PENDING_PATIENT,
      message: '',
      pendingAppointmentId: v.pendingAppointmentId,
      pendingPatient: v.pendingPatient
    } as any);
  };

  return (
    <Autocomplete
      open={searchMode === 'global' && popupOpen}
      onOpen={openPopup}
      onClose={closePopup}
      clearOnBlur={false}
      disableClearable={false}
      loading={loading && !!results.length}
      options={results}
      size="small"
      sx={{
        width: 220,
        '& .MuiOutlinedInput-root': {
          paddingRight: '12px!important'
        }
      }}
      filterOptions={(o) => o}
      isOptionEqualToValue={(opt, val) => opt.name === val.name}
      getOptionLabel={(option) => option.name}
      onChange={(e, v) => {
        setValue('');
        if (v?.pendingPatient) {
          showActivationDialog(v);
        } else if (v) {
          push(`/patients/${v.patientId}`);
        }
      }}
      inputValue={value}
      onInputChange={(e, v) => setValue(v)}
      renderInput={(params) => (
        <SearchField
          {...params}
          placeholder={translate('doctor-dashboard.searchPatient')}
          loading={loading}
          InputProps={{
            ...params.InputProps,
            endAdornment: <SearchTypeToggle visible={!globalSearchModeOnly} />
          }}
        />
      )}
      PopperComponent={StyledPopper}
      renderOption={(props, option, { inputValue }) => {
        const isByName = option.target === 'NAME';
        const matches =
          isByName && match(option.name, inputValue, { insideWords: true });
        const parts = matches
          ? parse(option.name, matches)
          : [{ text: option.name, highlight: false }];

        const isPendingPatient = !!option.pendingPatient;
        const cls = (props.className ?? '').concat(
          isPendingPatient ? ' pending-patient' : ''
        );

        return (
          <li
            {...props}
            key={option.patientId || option.pendingPatient?.id}
            className={cls}
          >
            <Stack>
              <div style={{ whiteSpace: 'nowrap' }}>
                {parts.map((part, index) => (
                  <Typography
                    data-sentry-mask
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    component="span"
                    fontWeight={part.highlight ? 700 : 400}
                    color="text.primary"
                  >
                    {part.text}
                  </Typography>
                ))}
              </div>
              <div style={{ marginTop: '-6px' }}>
                <Typography color="text.secondary" variant="caption">
                  {!option.birthDate && !option.gender && (
                    <div style={{ marginTop: '8px' }} />
                  )}
                  {option.birthDate && (
                    <>
                      <Typography
                        component="span"
                        variant="inherit"
                        data-sentry-mask
                      >
                        <TranslatedText id="doctor-dashboard.bday.abbr" />
                      </Typography>{' '}
                      <FormattedDate
                        fontSize="inherit"
                        color="inherit"
                        variant="inherit"
                        fontWeight={option.target === 'BIRTHDATE' ? 700 : 400}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component="span"
                      >
                        {option.birthDate}
                      </FormattedDate>
                    </>
                  )}
                  {option.birthDate && option.gender && (
                    <>&nbsp; &bull; &nbsp;</>
                  )}
                  {option.gender && (
                    <Typography component="span" variant="inherit">
                      <TranslatedText
                        id={`app.user.gender.${option.gender?.toLowerCase()}`}
                      />
                    </Typography>
                  )}
                </Typography>
              </div>
              {isPendingPatient && (
                <Chip
                  label={translate(
                    'doctor-dashboard.searchPatient.pendingPatient'
                  )}
                  clickable
                  color="warning"
                  size="small"
                  style={{ width: 'fit-content' }}
                />
              )}
            </Stack>
          </li>
        );
      }}
    />
  );
}

const StyledToggleButton = styled(ToggleButton)`
  padding: 0;
  border-radius: 999px;
  border: none;
  background-color: transparent !important;
`;

const SearchTypeToggle = ({ visible }: { visible: boolean }) => {
  const translate = useTranslator();
  const [updateSettings] = useUpdateUserAppSettingsMutation();
  const searchType = useUserAppSetting('patientSearch', 'global');
  const toggledOn = searchType === 'global';
  const toggle = () => {
    if (!visible) {
      return;
    }
    updateSettings({
      patientSearch: searchType === 'global' ? 'protocol' : 'global'
    });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          key="searchModeToggle"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
        >
          <Tooltip
            title={translate(
              toggledOn
                ? 'doctor-dashboard.searchPatient.protocol'
                : 'doctor-dashboard.searchPatient.hospital'
            )}
          >
            <StyledToggleButton
              value={'null'}
              selected={toggledOn}
              onClick={toggle}
              color={toggledOn ? 'primary' : 'standard'}
            >
              <HospitalIcon
                color={
                  toggledOn ? themeColors.indigo[500] : themeColors.slate[300]
                }
              />
            </StyledToggleButton>
          </Tooltip>
        </motion.span>
      )}
    </AnimatePresence>
  );
};
